import { APICall } from "@/config/NetworkUtility";
import { getErrorPayload } from "@/utils/reduxError";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const CACHE_TIME = 10 * 60 * 1000; // 10 minutes

export const getCompanyDropdownList = createAsyncThunk(
    "company/getCompanyDropdownList",
    async ({ forceRefresh = false } = {}, { getState, rejectWithValue }) => {
        try {
            const { companyDropdownList, companyDropdownFetchedAt } =
                getState().company;

            const isCacheValid =
                companyDropdownList.length > 0 &&
                companyDropdownFetchedAt &&
                Date.now() - companyDropdownFetchedAt < CACHE_TIME;

            // Return cached data instead of making API call
            if (!forceRefresh && isCacheValid) {
                return {
                    data: companyDropdownList,
                    fromCache: true,
                };
            }

            const result = await APICall(
                "get",
                {},
                "v1/CompanySetup/GetCompanyDropdownList",
            );

            if ([200, 201].includes(result?.statuscode)) {
                return {
                    data: result.data ?? [],
                    fromCache: false,
                };
            }

            return rejectWithValue({
                status: result?.statuscode,
                message: result?.message ?? "Failed to load company list.",
                data: result?.data ?? null,
            });
        } catch (error) {
            return rejectWithValue(getErrorPayload(error));
        }
    },
);

const initialState = {
    companyDropdownList: [],
    companyDropdownLoading: false,
    companyDropdownFetchedAt: null,
    error: null,
};

const companySlice = createSlice({
    name: "company",

    initialState,

    reducers: {
        clearCompanyDropdownList: (state) => {
            state.companyDropdownList = [];
            state.companyDropdownFetchedAt = null;
            state.companyDropdownLoading = false;
            state.error = null;
        },

        // Use this after adding a new company
        invalidateCompanyDropdownList: (state) => {
            state.companyDropdownFetchedAt = null;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(getCompanyDropdownList.pending, (state) => {
                state.companyDropdownLoading = true;
                state.error = null;
            })

            .addCase(getCompanyDropdownList.fulfilled, (state, action) => {
                state.companyDropdownLoading = false;
                state.companyDropdownList = action.payload.data;

                // Update timestamp only when data came from API
                if (!action.payload.fromCache) {
                    state.companyDropdownFetchedAt = Date.now();
                }
            })

            .addCase(getCompanyDropdownList.rejected, (state, action) => {
                state.companyDropdownLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearCompanyDropdownList,
    invalidateCompanyDropdownList,
} = companySlice.actions;

export default companySlice.reducer;